import { Link } from 'react-router';
import { useCanOpen } from '@/hooks/useCanOpen';
import { formatDate } from '@/utils/dateUtils';
import { diaperCheckDurationLine } from '@/types/diaperCheck';
import { DATE_PREFIX, FieldRow, clockTime, readString } from './payloadFields';

interface DiaperCheckPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

function readResolution(payload: Record<string, unknown>) {
  const raw = payload.resolution;
  if (typeof raw !== 'object' || raw === null) return undefined;
  const record = raw as Record<string, unknown>;
  return {
    activityType: readString(record, 'activity_type'),
    time: clockTime(readString(record, 'activity_time')),
    recordedBy: readString(record, 'recorded_by_name'),
  };
}

/**
 * Renders a `diaper_check_overdue` workflow item: a child flagged as using
 * diapers has no toilet entry for longer than the threshold. Leads with the
 * child. Times are org-local RFC 3339 strings read as text.
 */
export function DiaperCheckPayload({ payload, i18n }: DiaperCheckPayloadProps) {
  const canOpen = useCanOpen();
  const childId = readString(payload, 'child_id');
  const childName = readString(payload, 'child_name');
  const groupName = readString(payload, 'group_name');
  const leadName = readString(payload, 'lead_name');
  const headName = readString(payload, 'head_name');
  const day = readString(payload, 'day');
  const validDay = day && DATE_PREFIX.test(day) ? day.slice(0, 10) : undefined;
  const lastToilet = clockTime(readString(payload, 'last_toilet_time'));
  const dueAt = clockTime(readString(payload, 'due_at'));
  const handedAt = clockTime(readString(payload, 'handed_to_admins_at'));
  const endedAt = clockTime(readString(payload, 'ended_at'));
  const resolution = readResolution(payload);
  const durationLine = diaperCheckDurationLine(i18n, payload);

  const withDate = (time: string) =>
    validDay
      ? i18n._({
          id: 'workflow.payload.diaperCheck.dateTime',
          message: '{date}, {time}',
          values: { date: formatDate(validDay), time },
        })
      : time;

  const entryWord = (type: string | undefined): string => {
    switch (type) {
      case 'toilet_pee':
        return i18n._({ id: 'workflow.payload.diaperCheck.entry.pee', message: 'pee' });
      case 'toilet_poo':
        return i18n._({ id: 'workflow.payload.diaperCheck.entry.poo', message: 'poo' });
      case 'toilet_nothing':
        return i18n._({ id: 'workflow.payload.diaperCheck.entry.nothing', message: 'dry' });
      default:
        return '';
    }
  };

  const resolutionLine = (() => {
    if (!resolution?.time) return undefined;
    const values = { type: entryWord(resolution.activityType), name: resolution.recordedBy, time: resolution.time };
    return resolution.recordedBy
      ? i18n._({
          id: 'workflow.payload.diaperCheck.resolvedBy',
          message: 'Done: toilet entry ({type}) by {name} at {time}',
          values,
        })
      : i18n._({
          id: 'workflow.payload.diaperCheck.resolved',
          message: 'Done: toilet entry ({type}) at {time}',
          values,
        });
  })();

  const childLink =
    childId && validDay && canOpen('/activities')
      ? `/activities?child_id=${encodeURIComponent(childId)}&date_from=${validDay}&date_to=${validDay}`
      : undefined;

  return (
    <div className="space-y-4">
      <FieldRow label={i18n._({ id: 'workflow.payload.diaperCheck.child', message: 'Child' })}>
        <p className="text-sm font-medium" data-testid="diaper-check-child">
          {childName ?? '—'}
        </p>
        {durationLine && (
          <p className="text-sm" data-testid="diaper-check-duration">
            {durationLine}
          </p>
        )}
        {childLink && (
          <Link
            to={childLink}
            className="text-sm text-primary underline underline-offset-4"
          >
            {i18n._({
              id: 'workflow.payload.diaperCheck.openActivities',
              message: "Open the child's entries for this day",
            })}
          </Link>
        )}
      </FieldRow>

      {groupName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.diaperCheck.group', message: 'Group' })}>
          <p className="text-sm">{groupName}</p>
        </FieldRow>
      )}

      <FieldRow
        label={i18n._({ id: 'workflow.payload.diaperCheck.lastEntry', message: 'Last toilet entry' })}
      >
        <p className="text-sm">
          {lastToilet
            ? withDate(lastToilet)
            : i18n._({
                id: 'workflow.payload.diaperCheck.noEntryToday',
                message: 'None today',
              })}
        </p>
      </FieldRow>

      {dueAt && (
        <FieldRow label={i18n._({ id: 'workflow.payload.diaperCheck.dueAt', message: 'Due' })}>
          <p className="text-sm">{withDate(dueAt)}</p>
        </FieldRow>
      )}

      <FieldRow
        label={i18n._({ id: 'workflow.payload.diaperCheck.groupLead', message: 'Group lead' })}
      >
        <p className="text-sm">
          {leadName ??
            i18n._({
              id: 'workflow.payload.diaperCheck.noGroupLead',
              message: 'No group lead set',
            })}
        </p>
      </FieldRow>

      <FieldRow
        label={i18n._({ id: 'workflow.payload.diaperCheck.head', message: 'Daycare management' })}
      >
        <p className="text-sm">
          {headName ??
            i18n._({
              id: 'workflow.payload.diaperCheck.directToAdmin',
              message: 'Straight to the administration',
            })}
        </p>
      </FieldRow>

      {handedAt && (
        <FieldRow
          label={i18n._({
            id: 'workflow.payload.diaperCheck.handedToAdmins',
            message: 'Handed to the administration',
          })}
        >
          <p className="text-sm">{withDate(handedAt)}</p>
        </FieldRow>
      )}

      {endedAt && (
        <FieldRow label={i18n._({ id: 'workflow.payload.diaperCheck.endedAt', message: 'Ended' })}>
          <p className="text-sm">{withDate(endedAt)}</p>
        </FieldRow>
      )}

      {resolutionLine && (
        <p className="text-sm font-medium" data-testid="diaper-check-resolution">
          {resolutionLine}
        </p>
      )}
    </div>
  );
}
